import type { Language } from "@/hooks/useLanguage";

export type CookiesRows = {
  type: string;
  namePurpose: string;
  retention: string;
  essential: string;
};

export type CookiesLegal = {
  effectiveDate: string;
  title: string;
  subtitle: string;
  s1Title: string;
  s1Body: string;
  s2Title: string;
  s2p1: string;
  s2p2: string;
  s2p3: string;
  s2p4: string;
  s3Title: string;
  s3Intro: string;
  tableHeaders: { type: string; namePurpose: string; retention: string; essential: string };
  tableRows: CookiesRows[];
  s4Title: string;
  s4Body: string;
  s5Title: string;
  s5p1: string;
  s5p2: string;
  browsers: { chrome: string; firefox: string; safari: string; edge: string };
  s5WarnTitle: string;
  s5WarnBody: string;
  s6Title: string;
  s6Body: string;
  s7Title: string;
  s7p1: string;
  s7p2Prefix: string;
  privacyLinkLabel: string;
  s8Title: string;
  contactEmailLabel: string;
  closingPlaceDate: string;
};

export const cookiesTranslations: Record<Language, CookiesLegal> = {
  cs: {
    effectiveDate: "Účinné od 25. října 2026",
    title: "Zásady používání cookies",
    subtitle: "Platformy Zettl",
    s1Title: "1. Co jsou cookies",
    s1Body:
      "Cookies jsou malé textové soubory ukládané ve vašem prohlížeči při návštěvě webových stránek. Slouží k zajištění funkčnosti, bezpečnosti a uživatelského komfortu.",
    s2Title: "2. Právní základ a cookie banner (souhlas)",
    s2p1: "Podle čl. 5 odst. 3 směrnice Evropského parlamentu a Rady 2002/58/ES (tzv. ePrivacy) a jejího provedení v českém právu (zákon č. 127/2005 Sb., o elektronických komunikacích) platí, že ukládání informací do koncového zařízení nebo k nim přístup je ve výchozím stavu podmíněn souhlasem uživatele, s výjimkou případů, kdy je technické uložení nebo přístup nezbytně nutné pro přenos zprávy po síti elektronických komunikací, nebo je nezbytně nutné pro poskytnutí služby informační společnosti, kterou uživatel výslovně požadoval.",
    s2p2: "Bez souhlasu Platforma Zettl nastavuje jen tři cookies: jedna si pamatuje vaši volbu v cookie liště a dvě nastavení, které si sami zvolíte, tedy jazyk webu a sbalení bočního panelu (viz níže). Přihlášení drží úložiště prohlížeče (viz oddíl 6). Takové ukládání spadá do uvedené výjimky a nevyžaduje předchozí souhlas.",
    s2p3: "Analytické cookies (Google Analytics 4 a PostHog, viz oddíl 4) ukládáme jen s vaším předchozím souhlasem, který udělíte v cookie liště při první návštěvě. Dokud souhlas neudělíte, a pokud ho odmítnete, analytické nástroje se vůbec nenačtou. Odmítnout je stejně snadné jako přijmout. Marketingové ani reklamní cookies nepoužíváme.",
    s2p4: "Upozornění: Rámec ePrivacy se doplnil obecným nařízením o ochraně osobních údajů (GDPR). Cookies a úložiště prohlížeče mohou současně souviset se zpracováním osobních údajů podle GDPR; podrobnosti jsou v našich Zásadách ochrany osobních údajů.",
    s3Title: "3. Jaké cookies používáme",
    s3Intro:
      "V tabulce jsou všechny cookies, které Platforma nastavuje. Preferenční cookies vzniknou jen tehdy, když si dané nastavení sami změníte; analytické jen s vaším souhlasem. Přihlášení a další nastavení rozhraní ukládáme v úložišti prohlížeče — viz oddíl 6.",
    tableHeaders: {
      type: "Typ",
      namePurpose: "Název / účel",
      retention: "Doba platnosti",
      essential: "Ukládá se",
    },
    tableRows: [
      {
        type: "Nezbytná",
        namePurpose:
          "zettl_consent — vaše volba v cookie liště (zda souhlasíte s analytickými cookies), aby se lišta neukazovala znovu; platí pro zettl.cz i app.zettl.cz",
        retention: "6 měsíců",
        essential: "Vždy",
      },
      {
        type: "Preferenční",
        namePurpose: "sidebar:state — zda je boční panel aplikace sbalený, nebo rozbalený",
        retention: "7 dní",
        essential: "Na vaši žádost",
      },
      {
        type: "Preferenční",
        namePurpose:
          "zettl_lang — jazyk webu (cs/en), aby se úvodní stránka otevřela ve vámi zvoleném jazyce; uloží se jen po ručním přepnutí jazyka",
        retention: "1 rok",
        essential: "Na vaši žádost",
      },
      {
        type: "Analytická",
        namePurpose:
          "_ga — Google Analytics 4 (Google Ireland Limited): rozlišuje jednotlivé návštěvníky pro statistiky návštěvnosti",
        retention: "6 měsíců",
        essential: "Jen se souhlasem",
      },
      {
        type: "Analytická",
        namePurpose: "_ga_<ID> — Google Analytics 4: uchovává stav relace (návštěvy)",
        retention: "6 měsíců",
        essential: "Jen se souhlasem",
      },
      {
        type: "Analytická",
        namePurpose:
          "ph_<klíč projektu>_posthog — PostHog: anonymní identifikátor návštěvníka a relace pro statistiky používání webu a aplikace",
        retention: "6 měsíců",
        essential: "Jen se souhlasem",
      },
      {
        type: "Analytická",
        namePurpose:
          "zettl:first-touch (sessionStorage, vlastní) — která kampaň (utm parametry) nebo který web (jen název hostitele) vás přivedl k registraci; po registraci se uloží k vaší firmě, nic osobního, analytickým nástrojům se neposílá",
        retention: "Do zavření karty",
        essential: "Jen se souhlasem",
      },
    ],
    s4Title: "4. Analytické cookies (jen se souhlasem)",
    s4Body:
      "S vaším souhlasem měříme, které stránky a funkce se používají, abychom web a aplikaci mohli zlepšovat. Používáme k tomu Google Analytics 4 (poskytovatel Google Ireland Limited) a PostHog (PostHog, Inc., data uložená v EU). Měříme navštívené stránky (adresu bez parametrů a bez identifikátorů záznamů) a vybrané akce v aplikaci, například nahrání dokladů nebo export. Neodesíláme obsah dokladů, částky, čísla účtů, e-maily ani jména. Google Analytics používáme v režimu souhlasu (Consent Mode v2): reklamní úložiště a reklamní signály jsou vždy vypnuté a Google signals ani personalizace reklam nejsou zapnuté. Vercel Web Analytics a Speed Insights měří návštěvnost a rychlost webu bez cookies a bez ukládání do vašeho prohlížeče. Remarketingové ani reklamní cookies nepoužíváme.",
    s5Title: "5. Správa cookies",
    s5p1: "Souhlas s analytickými cookies můžete kdykoli udělit, změnit nebo odvolat odkazem „Nastavení cookies“ v patičce webu nebo v nabídce účtu v aplikaci. Odvoláním se analytické nástroje vypnou a jejich cookies smažeme; odvolání nemá vliv na zpracování před ním. Uložení přihlášení v prohlížeči (oddíl 6) nelze odmítnout, protože bez něj se do Platformy nelze přihlásit. Preferenční cookies vzniknou jen tehdy, když jazyk nebo boční panel sami přepnete.",
    s5p2: "Cookies můžete spravovat a mazat prostřednictvím nastavení svého prohlížeče:",
    browsers: {
      chrome: "Nastavení → Soukromí a zabezpečení → Cookies",
      firefox: "Možnosti → Soukromí a zabezpečení → Cookies",
      safari: "Předvolby → Soukromí → Cookies",
      edge: "Nastavení → Cookies a oprávnění webu",
    },
    s5WarnTitle: "Upozornění:",
    s5WarnBody:
      "Vymazáním úložiště prohlížeče se z Platformy odhlásíte; smazáním cookies se jazyk a boční panel vrátí do výchozího stavu.",
    s6Title: "6. Úložiště prohlížeče a webové fonty",
    s6Body:
      "Přihlášení do Platformy (přístupový a obnovovací token služby Supabase Auth) se ukládá v localStorage vašeho prohlížeče a uchovává se do odhlášení; je nezbytné pro poskytnutí služby, o kterou jste požádali. LocalStorage a sessionStorage Platforma využívá také pro zobrazovací preference a stav aplikace. Když v aplikaci nastane chyba, služba Sentry si v sessionStorage drží identifikátor relace, aby k hlášení chyby připojila záznam průběhu se skrytým textem; po zavření karty zmizí. Tyto technologie nejsou HTTP cookies a neslouží ke sledování napříč stránkami třetích stran. Platforma rovněž načítá webové fonty (Sora) z Google Fonts CDN — jde o síťové požadavky, nikoli cookies, ale mohou zahrnovat přenos vaší IP adresy společnosti Google LLC (předávání v rámci EU-USA Data Privacy Framework).",
    s7Title: "7. Změny zásad",
    s7p1: "O změnách těchto Zásad budeme informovat prostřednictvím oznámení na Platformě.",
    s7p2Prefix: "Více informací o zpracování osobních údajů naleznete v našich ",
    privacyLinkLabel: "Zásadách ochrany osobních údajů",
    s8Title: "8. Kontakt",
    contactEmailLabel: "E-mail:",
    closingPlaceDate: "V Praze dne 25. října 2026",
  },
  en: {
    effectiveDate: "Effective 25 October 2026",
    title: "Cookie policy",
    subtitle: "Zettl platform",
    s1Title: "1. What cookies are",
    s1Body:
      "Cookies are small text files stored in your browser when you visit websites. They are used to ensure functionality, security, and a usable experience.",
    s2Title: "2. Legal basis and cookie banner (consent)",
    s2p1: "Under Article 5(3) of Directive 2002/58/EC (the “ePrivacy” Directive) and its implementation in Czech law (Act No. 127/2005 Coll., on electronic communications), storing information on a terminal equipment or accessing it is generally subject to the user’s consent, except where storage or access is strictly necessary for transmitting a communication over an electronic communications network or strictly necessary to provide an information society service explicitly requested by the user.",
    s2p2: "Without consent the Zettl platform sets only three cookies: one remembers your choice in the cookie banner, and two remember a setting you choose yourself, the site language and whether the sidebar is collapsed (see below). Your sign-in is kept in browser storage (see section 6). Such storage falls under this exception and does not require prior consent.",
    s2p3: "We store analytics cookies (Google Analytics 4 and PostHog, see section 4) only with your prior consent, which you give in the cookie banner on your first visit. Until you consent, and if you decline, the analytics tools are not loaded at all. Declining is as easy as accepting. We do not use marketing or advertising cookies.",
    s2p4: "Note: The ePrivacy framework interacts with the General Data Protection Regulation (GDPR). Cookies and browser storage may still relate to personal data processing under the GDPR; see our Privacy Policy for details.",
    s3Title: "3. Cookies we use",
    s3Intro:
      "The table lists every cookie the platform sets. Preference cookies are created only when you change that setting yourself; analytics cookies only with your consent. Your sign-in and other interface settings are kept in browser storage — see section 6.",
    tableHeaders: {
      type: "Type",
      namePurpose: "Name / purpose",
      retention: "Lifetime",
      essential: "Set",
    },
    tableRows: [
      {
        type: "Necessary",
        namePurpose:
          "zettl_consent — your choice in the cookie banner (whether you accept analytics cookies), so the banner does not show again; valid for zettl.cz and app.zettl.cz",
        retention: "6 months",
        essential: "Always",
      },
      {
        type: "Preference",
        namePurpose: "sidebar:state — whether the app's sidebar is collapsed or expanded",
        retention: "7 days",
        essential: "At your request",
      },
      {
        type: "Preference",
        namePurpose:
          "zettl_lang — the site language (cs/en), so the home page opens in the language you chose; set only when you switch the language yourself",
        retention: "1 year",
        essential: "At your request",
      },
      {
        type: "Analytics",
        namePurpose:
          "_ga — Google Analytics 4 (Google Ireland Limited): tells visitors apart for traffic statistics",
        retention: "6 months",
        essential: "Only with consent",
      },
      {
        type: "Analytics",
        namePurpose: "_ga_<ID> — Google Analytics 4: keeps the state of the session (visit)",
        retention: "6 months",
        essential: "Only with consent",
      },
      {
        type: "Analytics",
        namePurpose:
          "ph_<project key>_posthog — PostHog: an anonymous visitor and session identifier for usage statistics of the website and app",
        retention: "6 months",
        essential: "Only with consent",
      },
      {
        type: "Analytics",
        namePurpose:
          "zettl:first-touch (sessionStorage, first-party) — which campaign (utm parameters) or which site (host name only) led you to register; after registration it is stored with your company, nothing personal, not sent to analytics tools",
        retention: "Until the tab is closed",
        essential: "Only with consent",
      },
    ],
    s4Title: "4. Analytics cookies (only with consent)",
    s4Body:
      "With your consent we measure which pages and features are used, so we can improve the website and the app. We use Google Analytics 4 (provider Google Ireland Limited) and PostHog (PostHog, Inc., data stored in the EU). We measure the pages visited (the address without parameters and without record identifiers) and selected actions in the app, for example uploading documents or an export. We do not send document contents, amounts, account numbers, e-mails or names. Google Analytics runs in Consent Mode v2: ad storage and ad signals are always off, and neither Google signals nor ad personalisation is enabled. Vercel Web Analytics and Speed Insights measure traffic and page speed without cookies and without storing anything in your browser. We do not use remarketing or advertising cookies.",
    s5Title: "5. Managing cookies",
    s5p1: "You can give, change or withdraw your consent to analytics cookies at any time with the “Cookie settings” link in the website footer or in the account menu in the app. Withdrawing turns the analytics tools off and we delete their cookies; it does not affect processing before the withdrawal. Keeping your sign-in in browser storage (section 6) cannot be declined, because you cannot sign in without it. Preference cookies are created only when you switch the language or the sidebar yourself.",
    s5p2: "You can manage or delete cookies via your browser settings:",
    browsers: {
      chrome: "Settings → Privacy and security → Cookies",
      firefox: "Settings → Privacy & Security → Cookies",
      safari: "Preferences → Privacy → Cookies",
      edge: "Settings → Cookies and site permissions",
    },
    s5WarnTitle: "Note:",
    s5WarnBody:
      "Clearing browser storage signs you out of the platform; deleting cookies resets the language and sidebar to their defaults.",
    s6Title: "6. Browser storage and web fonts",
    s6Body:
      "Your sign-in (the Supabase Auth access and refresh tokens) is stored in your browser's localStorage and kept until you sign out; it is strictly necessary to provide the service you asked for. The platform also uses localStorage and sessionStorage for display preferences and application state. When an error occurs in the app, Sentry keeps a session identifier in sessionStorage so it can attach a recording of the session, with text masked, to the error report; it is gone when you close the tab. These are not HTTP cookies and are not used for cross-site tracking. The platform also loads web fonts (Sora) from the Google Fonts CDN — these are network requests, not cookies, but may transmit your IP address to Google LLC (transfer under the EU-US Data Privacy Framework).",
    s7Title: "7. Changes to this policy",
    s7p1: "We will notify you of changes via notices on the platform.",
    s7p2Prefix: "More information on personal data processing is in our ",
    privacyLinkLabel: "Privacy Policy",
    s8Title: "8. Contact",
    contactEmailLabel: "Email:",
    closingPlaceDate: "Prague, 25 October 2026",
  },
};
